import Card, { CardHeader, CardBody } from '../../components/ui/Card';
import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listingsApi } from '../../api/listings';
import { verfahrenApi } from '../../api/verfahren';
import { vermieterApi } from '../../api/vermieter';
import { LISTING_STATES } from '../../types/index';
import type {
  Listing,
  CategorizedPeriod,
  MonthlyBreakdown,
  MonthlyBreakdownBucket,
  PeriodCategory,
  OverrideCategory,
  StayMetrics,
} from '../../types/index';
import StateBadge from '../../components/listings/StateBadge';
import PlatformBadges from '../../components/listings/PlatformBadges';
import ListingPlatformsCard from '../../components/listings/ListingPlatformsCard';
import PlatformOfferLink from '../../components/listings/PlatformOfferLink';
import VermieterPicker from '../../components/landlords/VermieterPicker';
import VerfahrenPicker from '../../components/proceedings/VerfahrenPicker';
import AddressHierarchySection from '../../components/addresses/AddressHierarchySection';
import EditableTextSection from '../../components/ui/EditableTextSection';
import { type StatusOption } from '../../components/status/StatusPicker';
import StateChangeWidget from '../../components/status/StateChangeWidget';
import AssignmentWidget from '../../components/assignments/AssignmentWidget';
import AufgabenBlock from '../../components/tasks/AufgabenBlock';
import AuditTimeline from '../../components/audit/AuditTimeline';
import DocumentList from '../../components/documents/DocumentList';
import KommunikationSection from '../../components/communication/KommunikationSection';
import BebauungsplanInfoCard from '../../components/maps/BebauungsplanInfoCard';
import CollapsibleSection from '../../components/ui/CollapsibleSection';
import ConfirmDeleteModal from '../../components/ui/ConfirmDeleteModal';
import MapLinkButton from '../../components/maps/MapLinkButton';
import { useIsModuleEnabled, useDocumentConfig } from '../../hooks/useConfigVisibility';
import { restoreOne } from '../../api/softDelete';
import { useCan } from '../../hooks/useCan';
import MonthGrid from '../../components/ui/MonthGrid';

const MONTH_NAMES = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

type CategoryKey = PeriodCategory;
type OverrideCat = OverrideCategory;

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  frei_buchbar: 'Frei & Buchbar',
  frei_nicht_buchbar: 'Frei & Nicht buchbar',
  nicht_verfuegbar: 'Nicht verfügbar',
  booking: 'Gebucht',
  blocker: 'Blockiert',
  unbekannt: 'Unbekannt',
};

const CATEGORY_DOT: Record<CategoryKey, string> = {
  frei_buchbar: 'bg-green-500',
  frei_nicht_buchbar: 'bg-sky-400',
  nicht_verfuegbar: 'bg-orange-700',
  booking: 'bg-red-500',
  blocker: 'bg-purple-500',
  unbekannt: 'bg-neutral-300',
};

/** Hintergrund-Farben für Bar/Heatmap (ohne Text). */
const CATEGORY_BAR: Record<CategoryKey, string> = {
  frei_buchbar: 'bg-green-400',
  frei_nicht_buchbar: 'bg-sky-300',
  nicht_verfuegbar: 'bg-orange-400',
  booking: 'bg-red-400',
  blocker: 'bg-purple-400',
  unbekannt: 'bg-neutral-300',
};



interface MonthHeatmapProps {
  year: number;
  monthIdx: number;
  monthLabel: string;
  dayCategoryMap: Map<string, PeriodCategory>;
  todayISO: string;
  selectionStart: string | null;
  selectionEnd: string | null;
  onDayClick: (iso: string) => void;
}

/** Kategorie-Kalender eines Monats. Die Rasterlogik liegt in `MonthGrid`;
 *  hier bleibt nur, was diese Seite ausmacht: Kategorie-Farben und die
 *  Bereichsauswahl per Klick. */
const MonthHeatmap: React.FC<MonthHeatmapProps> = ({
  year,
  monthIdx,
  monthLabel,
  dayCategoryMap,
  todayISO,
  selectionStart,
  selectionEnd,
  onDayClick,
}) => {
  // Selektionsspanne (sortiert), wenn beide Endpunkte gesetzt sind.
  const sortedRange: { from: string; to: string } | null = selectionStart && selectionEnd
    ? selectionStart <= selectionEnd
      ? { from: selectionStart, to: selectionEnd }
      : { from: selectionEnd, to: selectionStart }
    : null;

  return (
    <MonthGrid
      year={year}
      monthIdx={monthIdx}
      label={monthLabel}
      renderDay={({ iso, day }) => {
        const cat = dayCategoryMap.get(iso) ?? 'frei_buchbar';
        const isToday = iso === todayISO;
        const isAnchor = iso === selectionStart || iso === selectionEnd;
        const isInRange = sortedRange !== null && iso >= sortedRange.from && iso <= sortedRange.to;
        const label = CATEGORY_LABELS[cat];
        const ringClass = isAnchor
          ? 'ring-2 ring-blue-600 ring-offset-0'
          : isInRange
            ? 'ring-1 ring-blue-400'
            : isToday
              ? 'ring-1 ring-gray-900'
              : '';
        return {
          className: `${CATEGORY_BAR[cat]} ${ringClass}`,
          title: `${String(day).padStart(2, '0')}.${String(monthIdx + 1).padStart(2, '0')}.${year}: ${label}`,
          ariaLabel: `${day}.${monthIdx + 1}.${year} – ${label}`,
          onClick: () => onDayClick(iso),
        };
      }}
    />
  );
};

/** Admin-only: per-Inserat Cron-Override fürs Monitoring. Leer = Stadt-Standard. */
const MonitorScheduleEditor: React.FC<{ listing: Listing }> = ({ listing }) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(listing.detail_cron_schedule ?? '');
  const mutation = useMutation({
    mutationFn: () => listingsApi.setMonitorSchedule(listing.id, value.trim() === '' ? null : value.trim()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['listing'] }),
  });
  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Stadt-Standard"
        className="px-2 py-1 border border-gray-300 rounded font-mono text-xs w-40"
      />
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="btn btn-outline px-2 py-1 text-xs"
      >
        {mutation.isPending ? '…' : 'Speichern'}
      </button>
      {mutation.isSuccess && <span className="text-green-700 text-xs">✓ gespeichert</span>}
      {mutation.error && <span className="text-red-600 text-xs">Fehler</span>}
    </div>
  );
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => {
  if (!value) return null;
  return (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{value}</dd>
    </div>
  );
};

const ListingDetail: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get('returnTo');
  const returnTo = requestedReturnTo?.startsWith('/') && !requestedReturnTo.startsWith('//')
    ? requestedReturnTo
    : '/inserate';
  const queryClient = useQueryClient();
  const adressenVisible = useIsModuleEnabled('adressen');
  const verfahrenVisible = useIsModuleEnabled('verfahren');
  const vermieterVisible = useIsModuleEnabled('vermieter');
  const docTab = useDocumentConfig('inserat').tab;
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'dokumente' | 'verlauf'>('uebersicht');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const canRestore = useCan('restore_own');
  const canManageMonitoring = useCan('manage_cities');
  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreOne('inserat', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
    },
  });

  const { data: listing, isLoading, error } = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => listingsApi.getListing(Number(listingId)),
    enabled: !!listingId,
  });

  const { data: history } = useQuery({
    queryKey: ['listing', listingId, 'history'],
    queryFn: () => listingsApi.getHistory(Number(listingId)),
    enabled: !!listingId && activeTab === 'verlauf',
  });

  const { data: verfahrenDetail } = useQuery({
    queryKey: ['verfahren', listing?.verfahren_id],
    queryFn: () => verfahrenApi.getVerfahrenDetail(listing!.verfahren_id!),
    enabled: !!listing?.verfahren_id,
  });

  const { data: vermieterDetail } = useQuery({
    queryKey: ['vermieter', listing?.vermieter_id],
    queryFn: () => vermieterApi.getVermieterDetail(listing!.vermieter_id!),
    enabled: !!listing?.vermieter_id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Inserat konnte nicht geladen werden.</p>
          <button onClick={() => navigate(returnTo)} className="btn btn-text mt-4">
            Zurueck zur Uebersicht
          </button>
        </div>
      </div>
    );
  }

  const landlordTypPrefill = (() => {
    const raw = (listing.host_type || '').toLowerCase();
    if (raw === 'company' || raw === 'professional' || raw === 'gewerblich') {
      return 'gewerblich';
    }
    if (raw === 'private' || raw === 'privat' || raw === 'privatperson') {
      return 'privatperson';
    }
    return null;
  })();

  const landlordNamePrefill = listing.host_name;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate(returnTo)} className="btn btn-text mb-2 text-sm">
            &larr; Zurueck zur Uebersicht
          </button>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{listing.title}</h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StateBadge state={listing.state} />
                {listing.is_online ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-green-100 text-green-800">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    online
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-gray-100 text-gray-600"
                    title={listing.offline_since ? `Nicht mehr online seit ${new Date(listing.offline_since).toLocaleString('de-DE')}` : undefined}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    offline{listing.offline_since ? ` seit ${new Date(listing.offline_since).toLocaleDateString('de-DE')}` : ''}
                  </span>
                )}
                {(() => {
                  if (listing.merge_sources?.length) return null;
                  // Kanonischer Inserat-Link: HomeToGo-/rental/-Seite (Bilder, Details, Anbieter,
                  // „Zum Angebot"-Button). Kein direkter Plattform-Aufruf (Anti-Detection).
                  const linkUrl = listing.resolved_url || listing.listing_url;
                  return linkUrl ? (
                    <PlatformOfferLink
                      url={linkUrl}
                      provider={listing.platform}
                      title="Inserat auf HomeToGo ansehen (mit Bildern, Details und Absprung zur Plattform)"
                    />
                  ) : null;
                })()}
                {listing.address ? (
                  listing.adresse_id && adressenVisible ? (
                    <button
                      onClick={() => navigate(`/adressen/${listing.adresse_id}`)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 transition-colors bg-green-100 text-green-800 hover:bg-green-200"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      {listing.address}{listing.zipcode ? `, ${listing.zipcode}` : ''} &rarr;
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-amber-100 text-amber-800">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {listing.address}{listing.zipcode ? `, ${listing.zipcode}` : ''}
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-gray-100 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Keine Adresse
                  </span>
                )}
                {listing.latitude != null && listing.longitude != null && (
                  <MapLinkButton to={`/karte?listing=${listing.id}`} />
                )}
                {listing.registration_number && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-gray-100 text-gray-700">
                    Reg.-Nr.: {listing.registration_number}
                  </span>
                )}
                {listing.verfahren_id && verfahrenVisible && (
                  <button
                    onClick={() => navigate(`/verfahren/${listing.verfahren_id}`)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                    {verfahrenDetail?.aktenzeichen || `Verfahren #${listing.verfahren_id}`} &rarr;
                  </button>
                )}
                {listing.vermieter_id && vermieterVisible && (
                  <button
                    onClick={() => navigate(`/vermieter/${listing.vermieter_id}`)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-teal-100 text-teal-800 hover:bg-teal-200 transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    {vermieterDetail?.name || `Vermieter #${listing.vermieter_id}`} &rarr;
                  </button>
                )}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 flex flex-col gap-2 items-end">
              <div><StateChanger listing={listing} /></div>
              {listing.deleted_at ? (
                canRestore && (
                  <button
                    onClick={() => restoreMutation.mutate(listing.id)}
                    disabled={restoreMutation.isPending}
                    className="btn btn-secondary bg-success-50 text-success-700 border-success-200 enabled:hover:bg-success-100 focus-visible:ring-success-500 px-3 py-1.5 text-xs border"
                  >
                    Wiederherstellen
                  </button>
                )
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn btn-secondary bg-danger-50 text-danger-700 border-danger-200 enabled:hover:bg-danger-100 focus-visible:ring-danger-500 px-3 py-1.5 text-xs border"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {listing.merged_listing_id && (
        <section className="border-b border-blue-200 bg-blue-50">
          <div className="mx-auto max-w-5xl px-4 py-3 text-sm text-blue-950">
            Dieses Quellinserat wurde zusammengeführt.{' '}
            <button type="button" onClick={() => navigate(`/inserat/${listing.merged_listing_id}`)} className="btn btn-text hover:underline font-medium">
              Zusammengeführtes Inserat #{listing.merged_listing_id} öffnen
            </button>
          </div>
        </section>
      )}

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="inserat"
        ids={[listing.id]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['listings'] });
          navigate(returnTo);
        }}
      />

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-4">
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'uebersicht', label: 'Übersicht' },
              ...(docTab ? [{ key: 'dokumente', label: 'Dokumente' }] : []),
              { key: 'verlauf', label: 'Verlauf' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {activeTab === 'uebersicht' && (
          <div className="space-y-6">
            <AssignmentWidget
              entityType="inserat"
              entityId={listing.id}
              assignment={listing.assignment ?? null}
              invalidateKeys={[['listing', String(listing.id)], ['listings']]}
            />

            <AufgabenBlock
              entityType="inserat"
              entityId={listing.id}
              invalidateKeys={[['listing', String(listing.id)]]}
            />

            <ListingPlatformsCard listing={listing} />

            {/* 1. Verfahren */}
            {verfahrenVisible && <VerfahrenSection listing={listing} verfahrenAktenzeichen={verfahrenDetail?.aktenzeichen ?? null} />}

            {/* 2. Standort- und Objektdaten */}
            <AddressHierarchySection
              entityId={listing.id}
              isAssigned={listing.address_verified}
              initialAdresseId={listing.adresse_id}
              initialHausId={listing.haus_id}
              initialEinheitId={listing.einheit_id}
              prefillAddress={listing.address}
              prefillZipcode={listing.zipcode}
              prefillCity={listing.city}
              prefillEinheitTyp={listing.listing_type}
              prefillGuestsCount={listing.guests_count}
              prefillBedrooms={listing.bedrooms}
              prefillSizeSqm={listing.size ? listing.size.replace(/[^\d.,]/g, '').replace(',', '.') : undefined}
              scrapedHint={
                listing.scraped_address || listing.scraped_zipcode || listing.scraped_city
                  ? { address: listing.scraped_address, zipcode: listing.scraped_zipcode, city: listing.scraped_city }
                  : null
              }
              onSave={async (result) => {
                await listingsApi.updateListing(listing.id, {
                  address: result.address,
                  zipcode: result.zipcode ?? undefined,
                  city: result.city ?? undefined,
                  address_verified: true,
                  adresse_id: result.adresseId,
                  einheit_id: result.einheitId,
                  haus_id: result.hausId,
                });
                queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
                queryClient.invalidateQueries({ queryKey: ['listings'] });
              }}
              onHierarchyChange={(data) => {
                listingsApi.updateListing(listing.id, data).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
                  queryClient.invalidateQueries({ queryKey: ['listings'] });
                });
              }}
            />
            <BebauungsplanInfoCard
              latitude={listing.latitude}
              longitude={listing.longitude}
            />
            {/* Baugebiet — Inserat-specific, separate card. Per-city toggle. */}
            {listing.section_visibility?.baugebiet && (
              <EditableTextSection
                title="Baugebiet"
                value={listing.baugebiet || ''}
                placeholder="Nicht angegeben"
                onSave={async (baugebiet) => {
                  await listingsApi.updateListing(listing.id, { baugebiet: baugebiet.trim() || undefined });
                  queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
                  queryClient.invalidateQueries({ queryKey: ['listings'] });
                }}
              />
            )}

            {/* 3. Vermieter */}
            {/* The separate host hint remains hidden; scraped host data is used
              directly to prefill the required creation form instead. */}
            <VermieterPicker
              current={vermieterDetail ?? null}
              forceCreateWhenUnlinked
              createPrefill={landlordNamePrefill ? { name: landlordNamePrefill, typ: landlordTypPrefill } : undefined}
              onAssign={async (v) => {
                await listingsApi.updateListing(listing.id, { vermieter_id: v.id });
                queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
                queryClient.invalidateQueries({ queryKey: ['listings'] });
              }}
              onUnlink={async () => {
                await listingsApi.updateListing(listing.id, { vermieter_id: null });
                queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
                queryClient.invalidateQueries({ queryKey: ['listings'] });
              }}
            />

            {/* 4. Notizen */}
            <EditableTextSection
              title="Notizen"
              value={listing.notes || ''}
              placeholder="Keine Notizen"
              multiline
              onSave={async (notes) => {
                await listingsApi.updateListing(listing.id, { notes });
                queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
                queryClient.invalidateQueries({ queryKey: ['listings'] });
              }}
            />

            {/* 5. Registrierungsnummer — Per-city toggle. */}
            {listing.section_visibility?.registrierungsnummer && (
              <EditableTextSection
                title="Registrierungsnummer"
                value={listing.registration_number || ''}
                placeholder="Nicht angegeben"
                onSave={async (registration_number) => {
                  await listingsApi.updateListing(listing.id, { registration_number });
                  queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
                  queryClient.invalidateQueries({ queryKey: ['listings'] });
                }}
              />
            )}

            {/* 6. Buchungszeiträume */}
            {(listing.period_overrides || listing.availability || listing.categorized_periods?.length) && (
              <BuchungszeitraumeSection
                listingId={listing.id}
                periods={listing.categorized_periods}
                breakdown={listing.monthly_breakdown}
                staySummary={listing.stay_summary}
                stayMetrics={listing.stay_metrics}
              />
            )}

            {/* 7. Extrahierte Inseratdetails (+ Standort als Sub-Section) */}
            <Card>
              <CardHeader title="Extrahierte Inseratdetails" />
              <CardBody padding="horizontal">
                <dl className="divide-y divide-gray-200">
                  <InfoRow label="Typ" value={listing.listing_type} />
                  <InfoRow label="Größe" value={listing.size} />
                  <InfoRow label="Gäste" value={listing.guests_count} />
                  <InfoRow label="Schlafzimmer" value={listing.bedrooms} />
                  <InfoRow label="Bewertungen" value={(() => {
                    const reviews = listing.reviews && listing.reviews !== 'None' ? listing.reviews : null;
                    if (!listing.rating && !reviews) return 'Keine';
                    return (
                      <span className="inline-flex items-center gap-1.5">
                        {listing.rating && (
                          <span className="inline-flex items-center gap-0.5 font-semibold text-gray-900">
                            <span className="text-yellow-500">&#9733;</span>
                            {listing.rating.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                          </span>
                        )}
                        {reviews && (
                          <span className="text-gray-500">({reviews})</span>
                        )}
                      </span>
                    );
                  })()} />
                  <InfoRow label="Gastgeber" value={listing.host_name} />
                  {listing.host_type && (
                    <InfoRow label="Gastgeber-Typ" value={listing.host_type === 'company' ? 'Gewerblich' : 'Privat'} />
                  )}
                  <InfoRow label="Plattform" value={
                    <PlatformBadges platforms={listing.platforms} fallback={listing.platform} small={false} />
                  } />
                  <InfoRow label="Zuletzt geprüft" value={
                    listing.last_checked_at ? new Date(listing.last_checked_at).toLocaleString('de-DE') : null
                  } />
                  {canManageMonitoring && (
                    <InfoRow label="Überwachungs-Intervall (Cron)" value={<MonitorScheduleEditor listing={listing} />} />
                  )}
                  <InfoRow label="Preis/Nacht" value={listing.price_per_night ? `${listing.price_per_night} ${listing.currency}` : null} />
                  <InfoRow label="Auslastung" value={listing.occupancy_rate ? `${listing.occupancy_rate}%` : null} />
                </dl>

                {/* Standort Sub-Section */}
                <div className="border-t border-gray-200 mt-2 pt-4 pb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Standort</h4>
                  <dl className="divide-y divide-gray-200">
                    {listing.latitude && listing.longitude && (listing.scraped_address || listing.scraped_zipcode || listing.scraped_city) && (
                      <InfoRow label="Adresse (Koordinaten)" value={
                        [listing.scraped_address, listing.scraped_zipcode, listing.scraped_city].filter(Boolean).join(', ')
                      } />
                    )}
                    <InfoRow label="Stadtteil" value={listing.neighborhood} />
                    {listing.latitude && listing.longitude && (
                      <InfoRow label="Street View" value={
                        <a
                          href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${listing.latitude},${listing.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          In Google Street View ansehen &rarr;
                        </a>
                      } />
                    )}
                    {listing.latitude && listing.longitude && (
                      <InfoRow label="Satellitenansicht" value={
                        <a
                          href={`https://www.google.com/maps/@?api=1&map_action=map&center=${listing.latitude},${listing.longitude}&zoom=19&basemap=satellite`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          In Google Maps (Satellit) ansehen &rarr;
                        </a>
                      } />
                    )}
                  </dl>
                </div>
              </CardBody>
            </Card>

            {/* 8. Erfassungsdetails */}
            <Card>
              <CardHeader title="Erfassungsdetails" />
              <CardBody padding="horizontal">
                <dl className="divide-y divide-gray-200">
                  <InfoRow label="Online seit (erstmals gefunden)" value={new Date(listing.first_scraped_at).toLocaleString('de-DE')} />
                  {!listing.is_online && (
                    <InfoRow
                      label="Online bis"
                      value={listing.offline_since ? new Date(listing.offline_since).toLocaleString('de-DE') : 'Offline (Zeitpunkt unbekannt)'}
                    />
                  )}
                  <InfoRow label="Letzte Aktualisierung" value={listing.last_updated_at ? new Date(listing.last_updated_at).toLocaleString('de-DE') : 'Nie'} />
                  <InfoRow label="Belegungsdaten und Gastgeber finden" value={listing.detail_scraped_at ? new Date(listing.detail_scraped_at).toLocaleString('de-DE') : null} />
                </dl>
              </CardBody>
            </Card>

          </div>
        )}

        {activeTab === 'verlauf' && (
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Änderungsverlauf</h3>
            <AuditTimeline entries={history || []} />
          </Card>
        )}

        {activeTab === 'dokumente' && (
          <div className="space-y-6">
            <DocumentList entityType="inserat" entityId={listing.id} />
            <KommunikationSection
              entityType="inserat"
              entityId={listing.id}
              vermieterId={listing.vermieter_id ?? null}
            />
          </div>
        )}
      </main>
    </div>
  );
};

function StateChanger({ listing }: { listing: Listing }) {
  const queryClient = useQueryClient();

  const { data: statesConfig = [] } = useQuery({
    queryKey: ['listing-states'],
    queryFn: () => listingsApi.getStates(),
  });

  const mutation = useMutation({
    mutationFn: ({ state, comment }: { state: string; comment: string }) =>
      listingsApi.changeState(listing.id, state, comment || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
    },
  });

  const options: StatusOption[] = statesConfig.length > 0
    ? statesConfig
    : Object.entries(LISTING_STATES).map(([key, info]) => ({ key, label: info.label, color: info.color, recommended_transitions: [] }));

  return (
    <StateChangeWidget
      currentState={listing.state}
      options={options}
      onChangeState={(state, comment) => mutation.mutateAsync({ state, comment }).then(() => {})}
      isPending={mutation.isPending}
    />
  );
}

function VerfahrenSection({ listing, verfahrenAktenzeichen }: { listing: Listing; verfahrenAktenzeichen: string | null }) {
  const queryClient = useQueryClient();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const suggestStateMutation = useMutation({
    mutationFn: () => listingsApi.changeState(listing.id, 'verfahren_eingeleitet'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      setBannerDismissed(true);
    },
  });

  const showSuggestBanner =
    listing.verfahren_id !== null &&
    listing.state !== 'verfahren_eingeleitet' &&
    !bannerDismissed;

  const linkedExtra = showSuggestBanner ? (
    <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 gap-3">
      <p className="text-sm text-amber-800">
        Inserat-Status auf <strong>Verfahren eingeleitet</strong> ändern?
      </p>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={() => suggestStateMutation.mutate()}
          disabled={suggestStateMutation.isPending}
          className="btn btn-primary bg-warning-600 enabled:hover:bg-warning-700 focus-visible:ring-warning-500 px-3 py-1.5 text-xs"
        >
          Ja, ändern
        </button>
        <button
          onClick={() => setBannerDismissed(true)}
          className="btn btn-outline px-3 py-1.5 text-xs bg-white"
        >
          Nein
        </button>
      </div>
    </div>
  ) : undefined;

  return (
    <VerfahrenPicker
      current={listing.verfahren_id ? { id: listing.verfahren_id, aktenzeichen: verfahrenAktenzeichen } : null}
      onLinkExisting={async (verfahrenId) => {
        await verfahrenApi.attachInserat(verfahrenId, listing.id);
        queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
        queryClient.invalidateQueries({ queryKey: ['listings'] });
      }}
      onUnlink={async () => {
        if (!listing.verfahren_id) return;
        await verfahrenApi.detachInserat(listing.verfahren_id, listing.id);
        queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
        queryClient.invalidateQueries({ queryKey: ['listings'] });
      }}
      onCreateAndLink={async (data) => {
        await verfahrenApi.createVerfahren({ listing_id: listing.id, ...data });
        queryClient.invalidateQueries({ queryKey: ['listing', String(listing.id)] });
        queryClient.invalidateQueries({ queryKey: ['listings'] });
        queryClient.invalidateQueries({ queryKey: ['verfahren'] });
      }}
      linkedExtra={linkedExtra}
      unlinkedHint="Diesem Inserat ist noch kein Verfahren zugeordnet."
    />
  );
}

interface BuchungszeitraumeSectionProps {
  listingId: number;
  periods: CategorizedPeriod[] | null | undefined;
  breakdown: MonthlyBreakdown | null | undefined;
  staySummary: { min_nights: number | null; max_nights: number | null } | null | undefined;
  stayMetrics: StayMetrics | null | undefined;
}

const ZERO_BUCKET: MonthlyBreakdownBucket = {
  frei_buchbar: 0,
  frei_nicht_buchbar: 0,
  nicht_verfuegbar: 0,
  booking: 0,
  blocker: 0,
  unbekannt: 0,
};

const BUCKET_ORDER: (keyof MonthlyBreakdownBucket)[] = [
  'blocker',
  'booking',
  'nicht_verfuegbar',
  'unbekannt',
  'frei_nicht_buchbar',
  'frei_buchbar',
];

const BuchungszeitraumeSection: React.FC<BuchungszeitraumeSectionProps> = ({
  listingId,
  periods,
  breakdown,
  staySummary,
  stayMetrics,
}) => {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectionStart, setSelectionStart] = useState<string | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<string | null>(null);
  const [editorCategory, setEditorCategory] = useState<'booking' | 'blocker' | 'nicht_verfuegbar'>('booking');
  const [editorComment, setEditorComment] = useState('');
  const [editorError, setEditorError] = useState<string | null>(null);

  const clearSelection = () => {
    setSelectionStart(null);
    setSelectionEnd(null);
    setEditorCategory('booking');
    setEditorComment('');
    setEditorError(null);
  };

  const handleDayClick = (iso: string) => {
    if (!selectionStart) {
      setSelectionStart(iso);
      setSelectionEnd(null);
      setEditorError(null);
      return;
    }
    if (!selectionEnd) {
      setSelectionEnd(iso);
      return;
    }
    // Beide gesetzt → mit neuem Klick eine neue Auswahl starten.
    setSelectionStart(iso);
    setSelectionEnd(null);
    setEditorError(null);
  };

  const overrideAdd = useMutation({
    mutationFn: (payload: { from: string; to: string; category: OverrideCat; comment?: string }) =>
      listingsApi.addPeriodOverride(listingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', String(listingId)] });
    },
  });

  const overrideReset = useMutation({
    mutationFn: (payload: { from: string; to: string }) =>
      listingsApi.resetPeriodOverride(listingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listing', String(listingId)] });
    },
  });

  const safePeriods = periods ?? [];
  const safeBreakdown = breakdown ?? {};
  const years = Object.keys(safeBreakdown).sort();
  const hasData = years.length > 0;

  if (!hasData && safePeriods.length === 0) {
    return null;
  }

  const yearBreakdown = stayMetrics?.year_breakdown ?? {};
  const availableYears = Object.keys(yearBreakdown).sort();
  const defaultYear = stayMetrics?.current_year && yearBreakdown[stayMetrics.current_year]
    ? stayMetrics.current_year
    : availableYears[0];
  const activeYear = selectedYear && yearBreakdown[selectedYear] ? selectedYear : defaultYear;
  const yearData = activeYear ? yearBreakdown[activeYear] : null;

  const sortedCategories = yearData
    ? (Object.keys(yearData.by_category) as CategoryKey[])
        .filter((c) => yearData.by_category[c] > 0)
        .sort((a, b) => yearData.by_category[b] - yearData.by_category[a])
    : [];

  // Tag → Kategorie für die Mini-Heatmap pro Monat.
  // Tage, die nicht in einer Range stehen, sind implizit `frei_buchbar`
  // (suppressed im Backend aus dem Range-Listing).
  const dayCategoryMap = new Map<string, CategoryKey>();
  for (const period of safePeriods) {
    const start = new Date(period.from + 'T00:00:00');
    const end = new Date(period.to + 'T00:00:00');
    const current = new Date(start);
    while (current <= end) {
      const iso = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      dayCategoryMap.set(iso, period.category);
      current.setDate(current.getDate() + 1);
    }
  }

  const todayISO = new Date().toISOString().slice(0, 10);

  // Kompakte Zusammenfassung des aktuellen Jahres für den eingeklappten Zustand.
  const currentYear = stayMetrics?.current_year;
  const currentYearData = currentYear ? yearBreakdown[currentYear] : null;
  const summary = currentYearData && currentYearData.total_days > 0 ? (
    <div className="space-y-1.5 text-sm text-gray-700">
      <div className="flex h-2 w-full overflow-hidden rounded">
        {BUCKET_ORDER.map((c) => {
          const n = currentYearData.by_category[c];
          if (n <= 0) return null;
          return (
            <div
              key={c}
              className={CATEGORY_BAR[c]}
              style={{ width: `${(100 * n) / currentYearData.total_days}%` }}
              title={`${CATEGORY_LABELS[c]}: ${n}`}
            />
          );
        })}
      </div>
      <div className="text-xs text-gray-600">
        <span className="font-medium">{currentYear}:</span>{' '}
        {BUCKET_ORDER
          .filter((c) => currentYearData.by_category[c] > 0)
          .map((c) => `${currentYearData.by_category[c]} ${CATEGORY_LABELS[c]}`)
          .join(' · ')}
      </div>
    </div>
  ) : null;

  return (
    <CollapsibleSection title="Buchungszeiträume" summary={summary}>
      <>
        {stayMetrics && availableYears.length > 0 && (
          <div className="mt-3 rounded-md bg-gray-50 px-4 py-3">
                {/* Jahres-Tabs */}
                <div className="mt-3 flex items-center gap-1 flex-wrap">
                  <span className="text-xs text-gray-500 mr-1">Jahr:</span>
                  {availableYears.map((y) => {
                    const isActive = y === activeYear;
                    return (
                      <button
                        key={y}
                        onClick={() => setSelectedYear(y)}
                        className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                          isActive
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {y}
                      </button>
                    );
                  })}
                </div>

                {yearData && yearData.total_days > 0 && (
                  <>
                    {/* Gestackte horizontale Bar */}
                    <div className="mt-3 flex h-3 w-full overflow-hidden rounded">
                      {BUCKET_ORDER.map((c) => {
                        const n = yearData.by_category[c];
                        if (n <= 0) return null;
                        const widthPct = (100 * n) / yearData.total_days;
                        return (
                          <div
                            key={c}
                            className={CATEGORY_BAR[c]}
                            style={{ width: `${widthPct}%` }}
                            title={`${CATEGORY_LABELS[c]}: ${n} Tag(e) (${Math.round(widthPct)}%)`}
                          />
                        );
                      })}
                    </div>

                    {/* Kategorie-Zähler: zweispaltig, sortiert nach Häufigkeit */}
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      {sortedCategories.map((c) => {
                        const n = yearData.by_category[c];
                        const widthPct = Math.round((100 * n) / yearData.total_days);
                        return (
                          <div key={c} className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-gray-700">
                              <span className={`inline-block h-2.5 w-2.5 rounded-full ${CATEGORY_DOT[c]}`} />
                              {CATEGORY_LABELS[c]}
                            </span>
                            <span className="text-gray-900 font-medium tabular-nums">
                              {n} <span className="text-gray-500 font-normal">({widthPct}%)</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Buchungs-KPIs: jahres-spezifisch + global */}
                <div className="mt-3 text-xs text-gray-600 space-y-1">
                  {yearData && (
                    <div>
                      <span className="font-medium">In {activeYear}:</span>{' '}
                      {yearData.booking_signal_count} erkannte Buchung
                      {yearData.booking_signal_count !== 1 && 'en'}
                      {yearData.booking_signal_count > 0 && yearData.avg_booking_signal_nights !== null && (
                        <span className="text-gray-500"> (Ø {yearData.avg_booking_signal_nights} N.)</span>
                      )}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Gesamt:</span>
                    {stayMetrics.min_stay_platform !== null && (
                      <> Mindestaufenthalt {stayMetrics.min_stay_platform} N. · </>
                    )}
                    {stayMetrics.booking_signal_count_total} erkannte Buchung
                    {stayMetrics.booking_signal_count_total !== 1 && 'en'}
                    {stayMetrics.booking_signal_count_total > 0 && stayMetrics.avg_booking_signal_nights_total !== null && (
                      <span className="text-gray-500"> (Ø {stayMetrics.avg_booking_signal_nights_total} N.)</span>
                    )}
                  </div>
                </div>
          </div>
        )}

        <p className="mt-3 text-xs text-gray-500">
          Die Daten Frei/Nicht verfügbar kommen direkt von der Plattform. „Unbekannt" markiert Tage, für die keine Plattform-Daten vorliegen. Die Zeiträume können manuell bearbeitet werden.
        </p>
        {staySummary && (staySummary.min_nights || staySummary.max_nights) && !stayMetrics?.min_stay_platform && (
          <p className="mt-1 text-xs text-gray-600">
            <span className="font-medium">Aufenthalt laut Plattform:</span>
            {staySummary.min_nights !== null && <> mind. {staySummary.min_nights} Nächte</>}
            {staySummary.max_nights !== null && <> · max. {staySummary.max_nights} Nächte</>}
          </p>
        )}

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {(['frei_buchbar', 'frei_nicht_buchbar', 'nicht_verfuegbar', 'unbekannt', 'booking', 'blocker'] as const).map((cat) => (
            <span key={cat} className="inline-flex items-center gap-1.5">
              <span className={`inline-block h-2.5 w-2.5 rounded-full ${CATEGORY_DOT[cat]}`} />
              <span className="text-gray-600">{CATEGORY_LABELS[cat]}</span>
            </span>
          ))}
        </div>

        {years.map((year) => {
          const months = safeBreakdown[year];
          const totals: MonthlyBreakdownBucket = { ...ZERO_BUCKET };
          Object.values(months).forEach((m) => {
            BUCKET_ORDER.forEach((k) => { totals[k] += m[k] || 0; });
          });
          return (
            <div key={year} className="mb-4">
              <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
                <span className="text-sm font-semibold text-gray-700">{year}</span>
                <span className="text-xs text-gray-500">
                  {BUCKET_ORDER.filter((k) => totals[k] > 0)
                    .map((k) => `${totals[k]} ${CATEGORY_LABELS[k]}`)
                    .join(' · ')}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {MONTH_NAMES.map((monthLabel, monthIdx) => (
                  <MonthHeatmap
                    key={monthLabel}
                    year={parseInt(year, 10)}
                    monthIdx={monthIdx}
                    monthLabel={monthLabel}
                    dayCategoryMap={dayCategoryMap}
                    todayISO={todayISO}
                    selectionStart={selectionStart}
                    selectionEnd={selectionEnd}
                    onDayClick={handleDayClick}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Inline-Editor: erscheint, sobald ein Tag ausgewählt ist. */}
        {selectionStart && (
          <RangeEditor
            start={selectionStart}
            end={selectionEnd}
            category={editorCategory}
            comment={editorComment}
            error={editorError}
            isSaving={overrideAdd.isPending || overrideReset.isPending}
            onCategoryChange={setEditorCategory}
            onCommentChange={setEditorComment}
            onCancel={clearSelection}
            onSave={() => {
              const a = selectionStart;
              const b = selectionEnd ?? selectionStart;
              const from = a <= b ? a : b;
              const to = a <= b ? b : a;
              setEditorError(null);
              if (editorCategory === 'nicht_verfuegbar') {
                overrideReset.mutate(
                  { from, to },
                  {
                    onSuccess: clearSelection,
                    onError: (err: unknown) => setEditorError(
                      err instanceof Error ? err.message : 'Fehler beim Speichern.',
                    ),
                  },
                );
              } else {
                overrideAdd.mutate(
                  {
                    from,
                    to,
                    category: editorCategory,
                    comment: editorComment || undefined,
                  },
                  {
                    onSuccess: clearSelection,
                    onError: (err: unknown) => setEditorError(
                      err instanceof Error ? err.message : 'Fehler beim Speichern.',
                    ),
                  },
                );
              }
            }}
          />
        )}

        {!selectionStart && (
          <p className="mt-3 text-xs text-gray-500">
            Tipp: Klick auf einen Tag im Kalender wählt einen Start; ein zweiter Klick definiert das Ende. Anschließend lässt sich der Zeitraum als „Gebucht" oder „Blocker" einstufen oder zurück auf „Nicht verfügbar" setzen.
          </p>
        )}
      </>
    </CollapsibleSection>
  );
};

interface RangeEditorProps {
  start: string;
  end: string | null;
  category: 'booking' | 'blocker' | 'nicht_verfuegbar';
  comment: string;
  error: string | null;
  isSaving: boolean;
  onCategoryChange: (c: 'booking' | 'blocker' | 'nicht_verfuegbar') => void;
  onCommentChange: (s: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const RangeEditor: React.FC<RangeEditorProps> = ({
  start,
  end,
  category,
  comment,
  error,
  isSaving,
  onCategoryChange,
  onCommentChange,
  onSave,
  onCancel,
}) => {
  const fmt = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('de-DE');
  const effectiveEnd = end ?? start;
  const [from, to] = start <= effectiveEnd ? [start, effectiveEnd] : [effectiveEnd, start];
  const days = Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1;
  const isPending = end === null;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(); }}
      className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3"
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-sm">
          {isPending ? (
            <span className="text-blue-900">
              Start: <span className="font-medium">{fmt(start)}</span> · zweiten Tag im Kalender klicken, um den Zeitraum festzulegen.
            </span>
          ) : (
            <span className="text-blue-900">
              Zeitraum: <span className="font-medium">{fmt(from)} — {fmt(to)}</span> ({days} {days === 1 ? 'Tag' : 'Tage'})
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 text-xs"
        >
          Abbrechen
        </button>
      </div>

      {!isPending && (
        <>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <label className="flex flex-col">
              <span className="text-xs text-gray-600 mb-0.5">Kategorie</span>
              <select
                value={category}
                onChange={(e) => onCategoryChange(e.target.value as 'booking' | 'blocker' | 'nicht_verfuegbar')}
                className="border border-gray-300 rounded px-2 py-1 bg-white"
              >
                <option value="booking">Gebucht</option>
                <option value="blocker">Blockiert</option>
                <option value="nicht_verfuegbar">Nicht verfügbar (automatisch)</option>
              </select>
            </label>
            <label className="flex flex-col sm:col-span-2">
              <span className="text-xs text-gray-600 mb-0.5">Notiz (optional)</span>
              <input
                type="text"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder="z.B. Auskunft vom Vermieter"
                className="border border-gray-300 rounded px-2 py-1 bg-white"
              />
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            Wirkt nur auf Tage, die laut Plattform nicht frei sind. „Nicht verfügbar (Auto)" entfernt vorhandene manuelle Einstufungen in diesem Zeitraum.
          </p>
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          <div className="mt-3 flex gap-2">
            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-primary px-3 py-1.5"
            >
              {isSaving ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default ListingDetail;
